import React, { useState } from 'react';
import { cn } from '../../lib/utils';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  fallbackSrc?: string;
  placeholder?: boolean;
  className?: string;
  containerClassName?: string;
}

export function OptimizedImage({
  src,
  alt,
  fallbackSrc,
  placeholder = true,
  className,
  containerClassName,
  onError,
  onLoad,
  ...props
}: OptimizedImageProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoading(false);
    setHasError(false);
    onLoad?.(e);
  };
  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoading(false);
    
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setCurrentSrc(fallbackSrc);
      setHasError(false);
    } else if (currentSrc !== '/assets/images/Photo2.jpg' && !currentSrc.includes('Photo2.jpg')) {
      // Try Photo2 as ultimate local fallback
      setCurrentSrc('/assets/images/Photo2.jpg');
      setHasError(false);
      console.log('OptimizedImage: trying Photo2 as fallback');
    } else {
      setHasError(true);
    }
    
    onError?.(e);
  };

  if (hasError && !fallbackSrc) {
    return (
      <div className={cn(
        "flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800",
        className,
        containerClassName
      )}>
        <div className="text-center p-4">
          <svg 
            className="mx-auto h-8 w-8 text-gray-400 mb-2" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
          <p className="text-xs text-gray-500 font-medium">{alt}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative overflow-hidden", containerClassName)}>
      {isLoading && placeholder && (
        <div className={cn(
          "absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 animate-pulse",
          className
        )}>
          <div className="text-center">
            <div className="h-6 w-6 mx-auto mb-2 rounded-full bg-gray-300 dark:bg-gray-600 animate-pulse" />
            <div className="h-3 w-16 mx-auto bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
          </div>
        </div>
      )}
      
      <img
        {...props}
        src={currentSrc}
        alt={alt}
        className={cn(
          "transition-opacity duration-300",
          isLoading ? "opacity-0" : "opacity-100",
          className
        )}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
      />
    </div>
  );
}
